import {FormattedMessage, useIntl} from 'react-intl';
import React, {useState} from 'react';
import moment from 'moment';
import UpdateFixedPriceLockMutation from '../../mutations/update_fixed_price_lock_mutation';
import DeleteFixedPriceLockMutation from '../../mutations/delete_fixed_price_lock_mutation';
import {dispatch, EVENT_ID} from '../../containers/event_manager';
import Util from '../../forecast-app/shared/util/util';
import {FlexRow, Modal, SubHeading, Text, Toggle} from '@forecast-it/design-system';
import {getLockedPeriodByDate} from '../../components/new-ui/project/project-budget-v3/util/PeriodLocks';
import {RevenueMonthLockModalProps} from './RevenueMonthLockModal';

interface RevenueMonthUnlockModalProps extends RevenueMonthLockModalProps {
	currency: string;
}

export const RevenueMonthUnlockModal: React.FC<RevenueMonthUnlockModalProps> = ({
	closeModal,
	project,
	month,
	lockedAmounts,
	currency,
}) => {
	const intl = useIntl();
	const currencySymbol = Util.GetCurrencySymbol(currency);
	const [saving, setSaving] = useState(false);
	const lockedPeriods = project.fixedPriceLocks.edges.map(lockedPeriod => lockedPeriod.node);
	const lockedPeriod = getLockedPeriodByDate(moment(month), lockedPeriods);
	const manualRevenueRecognitionAmount =
		lockedAmounts.time.find(lockedAmount => lockedAmount.startDate.format('YYYY-MM') === month)?.amount ?? 0;
	const manualRevenueRecognition = Util.getFormattedNumberWithCurrency(currencySymbol, manualRevenueRecognitionAmount, intl);

	const onSuccess = () => {
		dispatch(EVENT_ID.BUDGET_UPDATE_PROJECT);
		closeModal();
	};

	const updateFixedPriceLock = () => {
		Util.CommitMutation(UpdateFixedPriceLockMutation, {id: lockedPeriod.id, locked: false}, onSuccess);
	};

	const resetManualRevenueRecognition = () => {
		Util.CommitMutation(DeleteFixedPriceLockMutation, {fixedPriceLockId: lockedPeriod.id}, onSuccess);
	};

	const [isClearManualRevRecChecked, setIsClearManualRevRecChecked] = useState<boolean>(true);

	const onSave = () => {
		setSaving(true);
		if (isClearManualRevRecChecked) {
			resetManualRevenueRecognition();
		} else {
			updateFixedPriceLock();
		}
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id={'fixed_price_lock.unlock_modal_button'} />
			</Modal.Title>
			<Modal.Content>
				<FlexRow>
					<SubHeading>
						<FormattedMessage
							id={'fixed_price_lock.unlock_message'}
							values={{
								month: moment(month, 'YYYY-MM').format('MMMM YYYY'),
							}}
						/>
					</SubHeading>
				</FlexRow>
				<FlexRow>
					<Toggle
						checked={isClearManualRevRecChecked}
						onChange={checked => setIsClearManualRevRecChecked(checked)}
						cy={'toggle-cy-delete-lock-tag'}
					>
						<Text>
							<FormattedMessage id={'fixed_price_lock.clear_rev_rec_toggle_label'} />
						</Text>
					</Toggle>
				</FlexRow>
				<FlexRow>
					<Text size={'2'}>
						<FormattedMessage
							id={'fixed_price_lock.clear_rev_rec_message'}
							values={{manualRevenueRecognition: manualRevenueRecognition}}
						/>
					</Text>
				</FlexRow>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onSave} dataCy={'modal-btn-update-fixed-price-lock'} disabled={saving}>
					<FormattedMessage id={'fixed_price_lock.unlock_modal_button'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
